// src/views/SdkTestViewTry.tsx
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
    Card,
    Form,
    Input,
    Button,
    Descriptions,
    Alert,
    Space,
    Typography,
    Row,
    Col,
    Spin,
    message,
    Divider,
    Tag,
    Switch,
} from 'antd';
import {
    ApiOutlined,
    KeyOutlined,
    LockOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
    SafetyCertificateOutlined,
    UnlockOutlined,
    CodeOutlined,
} from '@ant-design/icons';
import JSEncrypt from 'jsencrypt';
import { privateKeySdkDecode, publicKeySdkEncode, rsaKey2048, rsaKey4096, rsaKey8192 } from '../services/Macro';
import { encrypUtil } from '../utils/EncrypUtil';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface SdkTestViewTryState {
    loading: boolean;
    result: any;
    errorMessage: string;
    requestData: any;
    isEncrypted: boolean;
    enableEncryption: boolean;
}

interface SdkInfoRequest {
    appKey: string;
    appSecret: string;
    requestCode: string;
}

interface SdkInfoResponse {
    code: number;
    msg: string;
    data?: {
        appName?: string;
        packageName?: string;
        totalQuota?: number;
        usedQuota?: number;
        remainingQuota?: number;
        startTime?: string;
        endTime?: string;
        status?: number;
        encode?: string;
        [key: string]: any;
    };
}

class SdkTestViewTry extends React.Component<WithTranslation, SdkTestViewTryState> {

    state: SdkTestViewTryState = {
        loading: false,
        result: null,
        errorMessage: '',
        requestData: null,
        isEncrypted: false,
        enableEncryption: true,
    };

    generateRequestCode = (): string => {
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substring(2, 10);
        return `${timestamp}${random}`.toUpperCase();
    };

    getDefaultRequestCode = (): string => {
        return this.generateRequestCode();
    };

    // JSEncrypt 加密
    encodeRsa = (data: any): { encode: string } | null => {
        try {
            const jsonString = JSON.stringify(data);
            const encrypt = new JSEncrypt();
            encrypt.setPublicKey(publicKeySdkEncode);
            const encrypted = encrypt.encrypt(jsonString);
            if (!encrypted) {
                throw new Error('加密失败');
            }
            return { encode: encrypted };
        } catch (error: any) {
            message.error(`加密失败: ${error.message}`);
            return null;
        }
    };

    // JSEncrypt 解密
    decodeRsa = async (encryptedData: any) => {
        try {
            let encodeStr: string;
            if (typeof encryptedData === 'object' && encryptedData.encode) {
                encodeStr = encryptedData.encode;
            } else if (typeof encryptedData === 'string') {
                encodeStr = encryptedData;
            } else {
                return encryptedData;
            }

            console.log(`加密字符串(来自服务器):\n`, encodeStr);
            const decrypted = await encrypUtil.decodeWithEncryptRsa(encodeStr, rsaKey8192.privateKey);
            console.log(`解码数据(来自服务器):\n`, decrypted);
            // const decrypt = new JSEncrypt();
            // decrypt.setPrivateKey(privateKeySdkDecode);
            // const decrypted = decrypt.decrypt(encodeStr);
            if (!decrypted) {
                throw new Error('解密失败');
            }
            return JSON.parse(decrypted);
        } catch (error: any) {
            message.error(`解密失败: ${error.message}`);
            return null;
        }
    };

    testGetAuthInfo = async (values: any) => {
        const TAG = `SdkTestViewTry.testGetAuthInfo() - `;
        this.setState({ loading: true, errorMessage: '', result: null, isEncrypted: false });

        try {
            let requestCode = values.requestCode;

            const requestData: SdkInfoRequest = {
                appKey: values.appKey,
                appSecret: values.appSecret,
                requestCode: requestCode,
            };

            this.setState({ requestData });

            const { enableEncryption } = this.state;
            let requestBody;

            if (enableEncryption) {

                // 使用 encrypt-rsa 加解密实验
                // const encryptedText = await encrypUtil.encodeWithEncryptRsa(requestCode, rsaKey8192.publicKey, rsaKey8192.privateKey);
                // const encryptedText = await encrypUtil.encodeWithEncryptRsa(requestCode, rsaKey4096.publicKey, rsaKey4096.privateKey);
                const encryptedText = await encrypUtil.encodeWithEncryptRsa(requestCode, rsaKey2048.publicKey, rsaKey2048.privateKey);
                console.log(TAG, `EncryptedText Length:`, encryptedText.length);
                if (!encryptedText) {
                    throw new Error('RequestCode加密失败');
                }
                requestBody = {
                    appKey: requestData.appKey,
                    appSecret: requestData.appSecret,
                    requestCode: encryptedText,
                };

                // const encryptedRequestCode = this.encodeRsa({ requestCode: requestData.requestCode });
                // if (!encryptedRequestCode) {
                //     throw new Error('RequestCode加密失败');
                // }
                // requestBody = {
                //     appKey: requestData.appKey,
                //     appSecret: requestData.appSecret,
                //     requestCode: encryptedRequestCode.encode,
                // };
            } else {
                requestBody = requestData;
            }

            const baseUrl = 'https://ctrl.healthassistant.app/user/api/v1/dev/sdk';
            const api = `${baseUrl}/get_auth_info`;
            const response = await fetch(api, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result: SdkInfoResponse = await response.json();

            if (result.data && result.data.encode) {
                this.setState({ isEncrypted: true });
                const decryptedData = await this.decodeRsa(result.data);
                if (decryptedData) {
                    const originalEncode = result.data.encode;
                    const newData = {
                        ...result.data,
                        ...decryptedData,
                        _originalEncode: originalEncode,
                    };
                    delete newData.encode;
                    result.data = newData;
                }
            }

            this.setState({ result });

            if (result.code === 200) {
                message.success(`请求成功！${result.msg || '获取SDK信息成功'}`);
            } else {
                message.error(`请求失败：${result.msg || '未知错误'}`);
            }
        } catch (error: any) {
            const errorMsg = error.message || '网络请求失败';
            this.setState({ errorMessage: errorMsg });
            message.error(errorMsg);
        } finally {
            this.setState({ loading: false });
        }
    };

    renderResultData = () => {
        const { result, isEncrypted } = this.state;

        if (!result) return null;

        const isSuccess = result.code === 200;
        const data = result.data || {};

        return (
            <div style={{ marginTop: 16 }}>
                <Divider orientation="left">
                    <Space>
                        {isSuccess ? (
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        ) : (
                            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                        )}
                        <Text strong>响应结果</Text>
                        <Tag color={isSuccess ? 'success' : 'error'}>
                            {isSuccess ? '成功' : '失败'}
                        </Tag>
                        {isEncrypted && (
                            <Tag color="purple" icon={<SafetyCertificateOutlined />}>
                                已解密
                            </Tag>
                        )}
                    </Space>
                </Divider>

                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="状态码" span={2}>
                        <Text code>{result.code}</Text>
                        <Tag color={isSuccess ? 'success' : 'error'} style={{ marginLeft: 8 }}>
                            {isSuccess ? 'SUCCESS' : 'ERROR'}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="状态信息" span={2}>
                        <Text>{result.msg || '无'}</Text>
                    </Descriptions.Item>
                </Descriptions>

                {isSuccess && data && Object.keys(data).length > 0 && (
                    <>
                        <Divider orientation="left">
                            <InfoCircleOutlined /> SDK信息详情
                            {isEncrypted && (
                                <Tag color="green" style={{ marginLeft: 8 }}>
                                    已解密
                                </Tag>
                            )}
                        </Divider>
                        <Descriptions bordered size="small" column={2}>
                            {data.appName && (
                                <Descriptions.Item label="应用名称">
                                    <Text strong>{data.appName}</Text>
                                </Descriptions.Item>
                            )}
                            {data.packageName && (
                                <Descriptions.Item label="应用包名">
                                    <Text code>{data.packageName}</Text>
                                </Descriptions.Item>
                            )}
                            {data.totalQuota !== undefined && (
                                <Descriptions.Item label="总配额">
                                    <Tag color="blue">{data.totalQuota}</Tag>
                                </Descriptions.Item>
                            )}
                            {data.usedQuota !== undefined && (
                                <Descriptions.Item label="已使用">
                                    <Tag color="orange">{data.usedQuota}</Tag>
                                </Descriptions.Item>
                            )}
                            {data.remainingQuota !== undefined && (
                                <Descriptions.Item label="剩余配额">
                                    <Tag color={data.remainingQuota > 0 ? 'green' : 'red'}>
                                        {data.remainingQuota}
                                    </Tag>
                                </Descriptions.Item>
                            )}
                            {data.startTime && (
                                <Descriptions.Item label="开始时间">
                                    <Text>{new Date(data.startTime).toLocaleString()}</Text>
                                </Descriptions.Item>
                            )}
                            {data.endTime && (
                                <Descriptions.Item label="结束时间">
                                    <Text>{new Date(data.endTime).toLocaleString()}</Text>
                                </Descriptions.Item>
                            )}
                            {data.status !== undefined && (
                                <Descriptions.Item label="状态">
                                    <Tag color={data.status === 1 ? 'green' : 'red'}>
                                        {data.status === 1 ? '激活' : '停用'}
                                    </Tag>
                                </Descriptions.Item>
                            )}
                            {Object.keys(data).map(key => {
                                if (['appName', 'packageName', 'totalQuota', 'usedQuota', 'remainingQuota', 'startTime', 'endTime', 'status', '_originalEncode'].includes(key)) {
                                    return null;
                                }
                                return (
                                    <Descriptions.Item label={key} key={key}>
                                        <Text code>{typeof data[key] === 'object' ? JSON.stringify(data[key]) : String(data[key])}</Text>
                                    </Descriptions.Item>
                                );
                            })}
                            {data._originalEncode && (
                                <Descriptions.Item label="原始加密数据" span={2}>
                                    <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                                        {data._originalEncode}
                                    </Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </>
                )}

                {!isSuccess && (
                    <Alert
                        message="请求失败"
                        description={result.msg || '请检查参数是否正确'}
                        type="error"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                )}

                <Divider orientation="left">原始JSON数据</Divider>
                <pre style={{
                    background: '#f5f5f5',
                    padding: 16,
                    borderRadius: 8,
                    overflow: 'auto',
                    maxHeight: 400,
                    fontSize: 12,
                }}>
                    {JSON.stringify(result, null, 2)}
                </pre>
            </div>
        );
    };

    render() {
        const { t } = this.props;
        const { loading, errorMessage, requestData, enableEncryption } = this.state;

        return (
            <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
                <Row justify="center">
                    <Col xs={24} lg={20} xl={18}>
                        <Card
                            title={
                                <Space>
                                    <ApiOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                                    <span style={{ fontSize: 18, fontWeight: 600 }}>SDK 接口测试工具</span>
                                    <Tag color="blue">V0.0.5+</Tag>
                                    {enableEncryption ? (
                                        <Tag color="purple" icon={<SafetyCertificateOutlined />}>
                                            RSA加密
                                        </Tag>
                                    ) : (
                                        <Tag color="orange" icon={<UnlockOutlined />}>
                                            明文传输
                                        </Tag>
                                    )}
                                </Space>
                            }
                            bordered={false}
                            style={{
                                borderRadius: 16,
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            }}
                            extra={
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    接口名称: get_auth_info
                                </Text>
                            }
                        >
                            <Alert
                                message="接口说明"
                                description="获取SDK信息，包括应用名、包名、使用次数信息、有效期信息等。Request Code 使用 RSA 加密传输。"
                                type="info"
                                showIcon
                                style={{ marginBottom: 24 }}
                            />

                            <Card
                                type="inner"
                                title={
                                    <Space>
                                        <KeyOutlined />
                                        <span>请求参数</span>
                                        <Space style={{ marginLeft: 16 }}>
                                            <Switch
                                                checkedChildren="RSA加密"
                                                unCheckedChildren="明文传输"
                                                checked={enableEncryption}
                                                onChange={(checked) => this.setState({ enableEncryption: checked })}
                                            />
                                        </Space>
                                    </Space>
                                }
                                size="small"
                                style={{ marginBottom: 24 }}
                            >
                                <Form
                                    name="sdkTestForm"
                                    onFinish={this.testGetAuthInfo}
                                    layout="vertical"
                                    size="large"
                                    initialValues={{
                                        appKey: '',
                                        appSecret: '',
                                        requestCode: this.getDefaultRequestCode(),
                                    }}
                                >
                                    <Row gutter={16}>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="appKey"
                                                label={
                                                    <Space>
                                                        <KeyOutlined />
                                                        <span>App Key</span>
                                                        <Tag color="red" style={{ fontSize: 10 }}>必填</Tag>
                                                    </Space>
                                                }
                                                rules={[
                                                    { required: true, message: '请输入App Key' },
                                                    { min: 16, message: 'App Key长度不能少于16位' },
                                                ]}
                                                tooltip="开发者平台分配的唯一应用标识"
                                            >
                                                <Input
                                                    placeholder="请输入App Key"
                                                    prefix={<KeyOutlined style={{ color: '#bfbfbf' }} />}
                                                    autoComplete="off"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="appSecret"
                                                label={
                                                    <Space>
                                                        <LockOutlined />
                                                        <span>App Secret</span>
                                                        <Tag color="red" style={{ fontSize: 10 }}>必填</Tag>
                                                    </Space>
                                                }
                                                rules={[
                                                    { required: true, message: '请输入App Secret' },
                                                    { min: 16, message: 'App Secret长度不能少于16位' },
                                                ]}
                                                tooltip="开发者平台分配的应用密钥"
                                            >
                                                <Input.Password
                                                    placeholder="请输入App Secret"
                                                    prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                                                    autoComplete="off"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Form.Item
                                        name="requestCode"
                                        label={
                                            <Space>
                                                <CodeOutlined />
                                                <span>Request Code</span>
                                                <Tag color="red" style={{ fontSize: 10 }}>必填</Tag>
                                                {enableEncryption && (
                                                    <Tag color="purple">将加密</Tag>
                                                )}
                                            </Space>
                                        }
                                        rules={[
                                            { required: true, message: '请输入Request Code' },
                                        ]}
                                        extra={
                                            <Button
                                                type="link"
                                                size="small"
                                                onClick={() => {
                                                    const form = document.querySelector('form') as any;
                                                    if (form) {
                                                        const code = this.getDefaultRequestCode();
                                                        const input = form.querySelector('input[name="requestCode"]');
                                                        if (input) {
                                                            input.value = code;
                                                            this.setState({});
                                                        }
                                                    }
                                                }}
                                                style={{ padding: 0, height: 'auto' }}
                                            >
                                                生成随机Request Code
                                            </Button>
                                        }
                                    >
                                        <Input
                                            placeholder="请输入Request Code"
                                            prefix={<CodeOutlined style={{ color: '#bfbfbf' }} />}
                                            autoComplete="off"
                                        />
                                    </Form.Item>

                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <Space>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                loading={loading}
                                                icon={enableEncryption ? <SafetyCertificateOutlined /> : <UnlockOutlined />}
                                                size="large"
                                                style={{ height: 44, borderRadius: 8, minWidth: 120 }}
                                            >
                                                {loading ? '请求中...' : (enableEncryption ? '加密发送' : '明文发送')}
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    const form = document.querySelector('form') as any;
                                                    if (form) {
                                                        form.reset();
                                                        const code = this.getDefaultRequestCode();
                                                        const input = form.querySelector('input[name="requestCode"]');
                                                        if (input) {
                                                            input.value = code;
                                                        }
                                                        this.setState({ result: null, errorMessage: '' });
                                                    }
                                                }}
                                                size="large"
                                                style={{ height: 44, borderRadius: 8 }}
                                            >
                                                重置
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                </Form>
                            </Card>

                            {errorMessage && (
                                <Alert
                                    message="请求异常"
                                    description={errorMessage}
                                    type="error"
                                    showIcon
                                    closable
                                    style={{ marginBottom: 16 }}
                                    onClose={() => this.setState({ errorMessage: '' })}
                                />
                            )}

                            {loading && (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spin size="large" tip="正在请求SDK信息..." />
                                </div>
                            )}

                            {requestData && !loading && (
                                <Card
                                    type="inner"
                                    title={
                                        <Space>
                                            <span>请求参数</span>
                                            {enableEncryption ? (
                                                <Tag color="purple">已加密</Tag>
                                            ) : (
                                                <Tag color="orange">明文</Tag>
                                            )}
                                        </Space>
                                    }
                                    size="small"
                                    style={{ marginTop: 16 }}
                                >
                                    <Descriptions bordered size="small" column={2}>
                                        <Descriptions.Item label="App Key">
                                            <Text code>{requestData.appKey}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="App Secret">
                                            <Text code>******</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Request Code" span={2}>
                                            <Text code>{requestData.requestCode}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="加密状态" span={2}>
                                            {enableEncryption ? (
                                                <Tag color="purple">✓ 已使用RSA加密</Tag>
                                            ) : (
                                                <Tag color="orange">✗ 明文传输</Tag>
                                            )}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            )}

                            {!loading && this.renderResultData()}
                        </Card>

                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                © 2026 SDK Platform. All rights reserved. | 接口版本: V0.0.5+
                            </Text>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }
}

export const SdkTestPageTry = withTranslation()(SdkTestViewTry);